import type { PropPlayback } from "./catalogue.ts";
import { PACK_INDEX_WITH_MOST, type PackIndexEntry, type PackIndexPreview, type PackIndexWithFrame } from "./pack-index.ts";
import type { PackIndexFault, PackIndexTerrain } from "./pack-index-terrains.ts";
import type { PackIndexTilesetEntry } from "./pack-index-tilesets.ts";
import { isText, isWhole, readFaults, readTerrains, readTilesets } from "./pack-match-read.ts";
import { naturalCompare } from "./prop-listing.ts";

/**
 * The search of every pack's palette props (ADR-129 decision 2.1 and its dated notes of 2026-10-12): the index
 * file a host answers, read and checked, and the match over it. A typed word matches from the start of a word,
 * never inside one; a plural finds the singular and the reverse; every typed word must match somewhere. A match
 * in the name or the group ranks above one in a search word, then one in the category alone, then one in the
 * pack's own name or setting words alone. A whole word equal to the typed one, or its plural, stands above a word
 * that only starts with it: among the name and the search words that is decided first, so a prop whose word is
 * the typed one stands above a prop whose name only starts with it ("cart" lists a carriage whose word is cart
 * before a "cartoonish" wind, "grave" a grave before a "gravel" patch), and what matches by its category alone, and
 * then by its pack alone, still comes after both. Within one such tier the packs take turns, one result of each
 * a round (the dated note of 2026-10-13): the packs the world already lists an entry of take the first turns, then
 * the host's pack table order, which is the index's, and a pack's own results stand by name. Nothing here names a
 * pack, a word or a category.
 */

/**
 * The index file's schema version this reader takes.
 */
export const PACK_INDEX_FILE_SCHEMA = 3;

const ENTRIES_MOST = 65_536;
const PACKS_MOST = 1024;
const WORDS_MOST = 64;
const TILE_MOST = 65_535;
const STEM_LEAST = 4;
const DURATION_MOST = 600_000;
const PREVIEW_FILE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}\/[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

/**
 * One pack as the index file names it: its id, its display name and the words that say where it is set.
 */
export interface PackIndexFilePack {
  readonly id: string;
  readonly name: string;
  readonly setting: readonly string[];
}

/**
 * The index file: the side of a picture on a sheet and how many stand in a row, the packs in the host's table
 * order, every prop entry, every corner terrain (ADR-129 amendment, A4), every tileset (A5) and the packs whose
 * terrains could not be held.
 */
export interface PackIndexFile {
  readonly schemaVersion: number;
  readonly cell: number;
  readonly columns: number;
  readonly packs: readonly PackIndexFilePack[];
  readonly entries: readonly PackIndexEntry[];
  readonly terrains: readonly PackIndexTerrain[];
  readonly tilesets: readonly PackIndexTilesetEntry[];
  readonly faults: readonly PackIndexFault[];
}

/**
 * Where a search found its typed words, weakest first to strongest last by number: lower ranks first.
 */
export const MATCH_RANK = { name: 0, word: 1, category: 2, pack: 3 } as const;

/**
 * One result: the entry, its pack's display name, the rank of its weakest typed word, and whether every typed
 * word matched a whole word at that rank or better.
 */
export interface PackSearchResult {
  readonly entry: PackIndexEntry;
  readonly packName: string;
  readonly rank: number;
  readonly whole: boolean;
}

/**
 * An index made ready for searching: each entry with its words split once.
 */
export interface PreparedPackSearch {
  readonly entries: readonly { readonly entry: PackIndexEntry; readonly packName: string; readonly order: number; readonly fields: readonly (readonly string[])[] }[];
}

/**
 * Whether a value is a bounded list of bounded strings.
 *
 * @param value The value.
 * @returns True for such a list.
 */
function isWords(value: unknown): value is string[] {
  return Array.isArray(value) && value.length <= WORDS_MOST && value.every(isText);
}

/**
 * Whether a value is a strip's preview as the schema has it: a file named beside the index, in one directory of
 * plain characters, and one whole duration for each frame.
 *
 * @param value The value.
 * @param frames How many frames the entry plays.
 * @returns True for `{ file, durations }` that fits the entry.
 */
function isPreview(value: unknown, frames: number): value is PackIndexPreview {
  const preview = value as { file?: unknown; durations?: unknown } | null;
  return typeof preview === "object" && preview !== null && typeof preview.file === "string" && PREVIEW_FILE.test(preview.file) && Array.isArray(preview.durations) && preview.durations.length === frames && preview.durations.every((each) => isWhole(each, 1, DURATION_MOST));
}

/**
 * Whether a value is the list of frames an entry brings, as the schema has it: at least one and at most
 * `PACK_INDEX_WITH_MOST`, each a tileset, a tile, a name, a size and a frame count.
 *
 * @param value The value.
 * @returns True for such a list.
 */
function isBrought(value: unknown): value is PackIndexWithFrame[] {
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= PACK_INDEX_WITH_MOST &&
    value.every((item: unknown) => {
      const each = item as Partial<Record<keyof PackIndexWithFrame, unknown>> | null;
      const size = each?.size;
      return typeof each === "object" && each !== null && isText(each.tileset) && each.tileset !== "" && isWhole(each.tile, 0, TILE_MOST) && isText(each.name) && Array.isArray(size) && size.length === 2 && isWhole(size[0], 1, 65_536) && isWhole(size[1], 1, 65_536) && isWhole(each.frames, 1, 65_536);
    })
  );
}

/**
 * Reads an index file as a server answered it, keeping the fields it knows and nothing else.
 *
 * @param value The parsed JSON.
 * @returns The file.
 * @throws Error naming the first field that is not as the schema has it: a schema version this reader does not
 *   take, a bad cell or column count, a pack or an entry with a missing or malformed field, an entry of a pack
 *   the file does not list, or more packs or entries than a file may hold.
 */
export function readPackIndexFile(value: unknown): PackIndexFile {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("the pack index is not an object");
  }
  const file = value as { schemaVersion?: unknown; cell?: unknown; columns?: unknown; packs?: unknown; entries?: unknown; terrains?: unknown; tilesets?: unknown; faults?: unknown };
  if (file.schemaVersion !== PACK_INDEX_FILE_SCHEMA) {
    throw new Error(`the pack index's schemaVersion is not ${PACK_INDEX_FILE_SCHEMA}`);
  }
  if (!isWhole(file.cell, 1, 256)) {
    throw new Error("the pack index's cell is not a size in pixels");
  }
  if (!isWhole(file.columns, 1, 1024)) {
    throw new Error("the pack index's columns is not a count");
  }
  if (!Array.isArray(file.packs) || file.packs.length > PACKS_MOST) {
    throw new Error("the pack index's packs is not a list");
  }
  const packs = file.packs.map((item: unknown, index): PackIndexFilePack => {
    const pack = item as { id?: unknown; name?: unknown; setting?: unknown } | null;
    if (typeof pack !== "object" || pack === null || !isText(pack.id) || pack.id === "" || !isText(pack.name) || !isWords(pack.setting)) {
      throw new Error(`the pack index's packs[${index}] is not { id, name, setting }`);
    }
    return { id: pack.id, name: pack.name, setting: [...pack.setting] };
  });
  if (!Array.isArray(file.entries) || file.entries.length > ENTRIES_MOST) {
    throw new Error("the pack index's entries is not a list");
  }
  const known = new Set(packs.map((pack) => pack.id));
  const plays: readonly string[] = ["static", "loop", "once"];
  const entries = file.entries.map((item: unknown, index): PackIndexEntry => {
    const entry = item as Partial<Record<keyof PackIndexEntry, unknown>> | null;
    const size = entry?.size;
    const sized = Array.isArray(size) && size.length === 2 && isWhole(size[0], 1, 65_536) && isWhole(size[1], 1, 65_536);
    if (
      typeof entry !== "object" ||
      entry === null ||
      !isText(entry.id) ||
      !isText(entry.pack) ||
      !known.has(entry.pack) ||
      !isText(entry.tileset) ||
      !isWhole(entry.tile, 0, TILE_MOST) ||
      !isText(entry.name) ||
      !isText(entry.group) ||
      !isWords(entry.words) ||
      (entry.category !== undefined && !isText(entry.category)) ||
      (entry.path !== undefined && !isText(entry.path)) ||
      !sized ||
      !isWhole(entry.frames, 1, 65_536) ||
      typeof entry.plays !== "string" ||
      !plays.includes(entry.plays) ||
      !isWhole(entry.cell, 0, ENTRIES_MOST) ||
      (entry.preview !== undefined && !isPreview(entry.preview, entry.frames as number)) ||
      (entry.with !== undefined && !isBrought(entry.with))
    ) {
      throw new Error(`the pack index's entries[${index}] is not an entry of a listed pack`);
    }
    const [width, height] = size as [number, number];
    return { id: entry.id, pack: entry.pack, tileset: entry.tileset, tile: entry.tile, name: entry.name, group: entry.group, words: [...entry.words], ...(entry.category === undefined ? {} : { category: entry.category }), ...(entry.path === undefined || entry.path === "" ? {} : { path: entry.path }), size: [width, height], frames: entry.frames, plays: entry.plays as PropPlayback, cell: entry.cell, ...(entry.preview === undefined ? {} : { preview: { file: (entry.preview as PackIndexPreview).file, durations: [...(entry.preview as PackIndexPreview).durations] } }), ...(entry.with === undefined ? {} : { with: (entry.with as PackIndexWithFrame[]).map((each) => ({ tileset: each.tileset, tile: each.tile, name: each.name, size: [each.size[0], each.size[1]] as [number, number], frames: each.frames })) }) };
  });
  const terrains = readTerrains(file.terrains, known);
  const tilesets = readTilesets(file.tilesets, known);
  const faults = readFaults(file.faults, known);
  return { schemaVersion: PACK_INDEX_FILE_SCHEMA, cell: file.cell, columns: file.columns, packs, entries, terrains, tilesets, faults };
}

/**
 * A word with its plural ending taken off, so a plural finds the singular and the reverse: `es` after s, x, z, ch
 * or sh, else a lone `s`. A word of fewer than four letters, or one in `ss`, is left as it is.
 *
 * @param word A lowercase word.
 * @returns Its stem.
 */
export function wordStem(word: string): string {
  if (word.length < STEM_LEAST) {
    return word;
  }
  if (/(?:s|x|z|ch|sh)es$/.test(word)) {
    return word.slice(0, -2);
  }
  return word.endsWith("s") && !word.endsWith("ss") ? word.slice(0, -1) : word;
}

/**
 * The words of a text, lowercase, split at anything that is not a letter or a digit.
 *
 * @param text The text.
 * @returns Its words.
 */
function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word !== "");
}

/**
 * Makes an index ready for searching: each entry's name and group, its search words, its category, and its
 * pack's name and setting words, each split into words once.
 *
 * @param file The index file.
 * @returns The prepared index, its entries in the file's order.
 */
export function preparePackSearch(file: PackIndexFile): PreparedPackSearch {
  const packs = new Map(file.packs.map((pack, order) => [pack.id, { name: pack.name, order, words: [...wordsOf(pack.name), ...pack.setting.flatMap(wordsOf)] }]));
  return {
    entries: file.entries.map((entry) => {
      const pack = packs.get(entry.pack);
      return { entry, packName: pack?.name ?? entry.pack, order: pack?.order ?? packs.size, fields: [[...wordsOf(entry.name), ...wordsOf(entry.group)], entry.words.flatMap(wordsOf), wordsOf(entry.category ?? ""), pack?.words ?? []] };
    }),
  };
}

/**
 * How well one typed word matches one field: 0 for a whole word, 1 for a word that starts with it, null for
 * neither.
 *
 * @param field The field's words.
 * @param typed The typed word, lowercase.
 * @param stem Its stem.
 * @returns The match, or null.
 */
function fieldMatch(field: readonly string[], typed: string, stem: string): 0 | 1 | null {
  let best: 0 | 1 | null = null;
  for (const word of field) {
    if (word === typed || wordStem(word) === stem) {
      return 0;
    }
    if (word.startsWith(stem)) {
      best = 1;
    }
  }
  return best;
}

/**
 * Where a result stands by how it matched, lower first: a whole word in the name, a whole word among the search
 * words, a word start in the name, a word start among the search words, then the category (whole, then start),
 * then the pack (whole, then start).
 *
 * @param result The result.
 * @returns Its tier, from 0.
 */
function matchTier(result: Pick<PackSearchResult, "rank" | "whole">): number {
  if (result.rank <= MATCH_RANK.word) {
    return (result.whole ? 0 : 2) + result.rank;
  }
  return result.rank * 2 + (result.whole ? 0 : 1);
}

/**
 * Orders two entries by name in natural order, so a number in a name counts as a number and letter case is set
 * aside (`bed_2` before `bed_10`), then by their place in their pack.
 *
 * @param a One entry.
 * @param b Another.
 * @returns Negative when `a` stands first.
 */
function byName(a: PackIndexEntry, b: PackIndexEntry): number {
  return naturalCompare(a.name, b.name) || a.cell - b.cell;
}

/**
 * Searches a prepared index.
 *
 * @param index The prepared index.
 * @param query The typed text; its words are split at spaces and each must match.
 * @param world What the world already has: `offered` answers whether it offers an entry, which is then left
 *   out, and `listedPacks` holds the packs it lists an entry of, which take the first turns within a tier.
 * @returns The results in order; none for a query with no word.
 */
export function searchPacks(index: PreparedPackSearch, query: string, world: { readonly offered: (entry: PackIndexEntry) => boolean; readonly listedPacks: ReadonlySet<string> }): PackSearchResult[] {
  const typed = wordsOf(query).map((word) => ({ word, stem: wordStem(word) }));
  if (typed.length === 0) {
    return [];
  }
  const results: (PackSearchResult & { readonly order: number })[] = [];
  for (const candidate of index.entries) {
    let weakest = -1;
    for (const { word, stem } of typed) {
      let best = -1;
      for (let rank = 0; rank < candidate.fields.length; rank += 1) {
        const match = fieldMatch(candidate.fields[rank] as readonly string[], word, stem);
        if (match !== null) {
          const score = rank * 2 + match;
          best = best < 0 || score < best ? score : best;
          if (match === 0) {
            break;
          }
        }
      }
      if (best < 0) {
        weakest = -1;
        break;
      }
      weakest = Math.max(weakest, best);
    }
    if (weakest < 0 || world.offered(candidate.entry)) {
      continue;
    }
    results.push({ entry: candidate.entry, packName: candidate.packName, rank: Math.floor(weakest / 2), whole: weakest % 2 === 0, order: candidate.order });
  }
  const listed = (result: PackSearchResult): number => (world.listedPacks.has(result.entry.pack) ? 0 : 1);
  results.sort((a, b) => matchTier(a) - matchTier(b) || listed(a) - listed(b) || a.order - b.order || byName(a.entry, b.entry));
  return packsInTurn(results).map(({ entry, packName, rank, whole }) => ({ entry, packName, rank, whole }));
}

/**
 * Lists the whole index with no typed text: every entry the world does not offer, the packs in the host's table
 * order, then the category (an entry with none after those that have one), then the name in natural order.
 *
 * @param index The prepared index.
 * @param world What the world already has: `offered` answers whether it offers an entry, which is then left out.
 * @returns The entries as results of a rank-0 whole match, in that order.
 */
export function listPacks(index: PreparedPackSearch, world: { readonly offered: (entry: PackIndexEntry) => boolean }): PackSearchResult[] {
  const listed = index.entries.filter((candidate) => !world.offered(candidate.entry));
  const category = (entry: PackIndexEntry): string => entry.category ?? "";
  listed.sort((a, b) => a.order - b.order || Number(category(a.entry) === "") - Number(category(b.entry) === "") || naturalCompare(category(a.entry), category(b.entry)) || byName(a.entry, b.entry));
  return listed.map(({ entry, packName }) => ({ entry, packName, rank: MATCH_RANK.name, whole: true }));
}

/**
 * Deals each tier's results a round at a time, one of each pack a round, so a pack with many results of a tier
 * does not hold the list to its end: the packs keep the order the tier gives them, and each pack's own results
 * keep theirs.
 *
 * @param sorted The results, sorted by tier, then pack, then name.
 * @returns The same results, each tier's packs taken in turn.
 */
function packsInTurn<Result extends PackSearchResult>(sorted: readonly Result[]): Result[] {
  const dealt: Result[] = [];
  let from = 0;
  while (from < sorted.length) {
    const tier = matchTier(sorted[from] as Result);
    const packs = new Map<string, Result[]>();
    let to = from;
    for (; to < sorted.length && matchTier(sorted[to] as Result) === tier; to += 1) {
      const result = sorted[to] as Result;
      const own = packs.get(result.entry.pack);
      if (own === undefined) {
        packs.set(result.entry.pack, [result]);
      } else {
        own.push(result);
      }
    }
    const hands = [...packs.values()];
    for (let round = 0; dealt.length < to; round += 1) {
      for (const hand of hands) {
        if (round < hand.length) {
          dealt.push(hand[round] as Result);
        }
      }
    }
    from = to;
  }
  return dealt;
}
